export type ScrollController = {
	stop: () => void;
	start: () => void;
	/** `offset` in px above the target, so a caller can clear a fixed header without measuring it. */
	scrollTo: (target: HTMLElement, offset: number) => void;
};

let controller: ScrollController | undefined;
let owner: HTMLElement | undefined;

export const setScrollController = (
	next: ScrollController | undefined,
	element?: HTMLElement,
): void => {
	controller = next;
	owner = next === undefined ? undefined : element;
};

/* Drops a controller whose element has left the document: a teardown that never ran otherwise
   leaves a destroyed Lenis registered, and `scrollTo` on one is a silent no-op. */
export const getScrollController = (): ScrollController | undefined => {
	if (owner !== undefined && !owner.isConnected) setScrollController(undefined);

	return controller;
};

const HEADER_BAR = "[data-fa-header-bar]";

/* `bottom`, never `height`: the collapse translates the bar up out of a header box that keeps its
   full height, so the two differ by the utility row and only the edge is where the bar ends. */
export const headerOffset = (root: ParentNode = document): number =>
	root.querySelector<HTMLElement>(HEADER_BAR)?.getBoundingClientRect().bottom ?? 0;

export const scrollToElement = (target: HTMLElement, offset = 0): void => {
	const controller = getScrollController();

	if (controller) {
		controller.scrollTo(target, offset);
		return;
	}

	window.scrollTo({
		top: target.getBoundingClientRect().top + window.scrollY - offset,
		/* Omitted, this resolves to `auto` — the vendor's `scroll-behavior` would animate it. */
		behavior: "instant",
	});
};
